import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAccess, type Feature } from "@/hooks/use-access";

function AccessLoading() {
	return (
		<div className="flex min-h-screen flex-1 items-center justify-center bg-bg-canvas">
			<Loader2 size={24} className="animate-spin text-text-tertiary" />
		</div>
	);
}

type RequireAccessProps = {
	children: ReactNode;
	/**
	 * For pages opened by a project or file id: allow when the feature is in any
	 * of the user's spaces, because the item may belong to another space than
	 * the one chosen in the switcher. The API checks the item's own space.
	 */
	anySpace?: boolean;
} & ({ feature: Feature } | { anyOf: Feature[] } | { superAdmin: true });

/**
 * Renders the page only when the user may open it. Otherwise sends them to
 * their main page (the one they land on after login), which is always a page
 * they can open, so this never loops.
 */
export function RequireAccess(props: RequireAccessProps) {
	const access = useAccess();
	if (access.isLoading) return <AccessLoading />;

	const wanted = "feature" in props ? [props.feature] : "anyOf" in props ? props.anyOf : null;
	const allowed = wanted
		? props.anySpace
			? access.hasAnySpace(...wanted)
			: access.hasAny(...wanted)
		: access.isSuperAdmin;

	return allowed ? <>{props.children}</> : <Navigate to={access.homePath} replace />;
}

/**
 * Sends the user to their main page: the first page they can open. Used for
 * "/", after login, and for pages that are closed to everyone.
 */
export function HomeRedirect() {
	const { isLoading, homePath } = useAccess();
	if (isLoading) return <AccessLoading />;
	return <Navigate to={homePath} replace />;
}
